import { formatThaiCalendarDate, formatBangkokDateTime } from '../../utilities/bangkokDateTime';
import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PaidIcon from "@mui/icons-material/Paid";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import NavigationBar from "../../components/global/Layout/MainLayout/NavigationBar";
import { ActionPaper } from "../../components/global/Widget";
import { BasicTable } from "../../components/global/table";
import {
  useCreateBillingPayment,
  useGetBillingCustomerInvoices,
  useGetBillingPayments,
  useGetBillingRounds,
  useGetCustomersBillingSummary,
  useUploadPaymentEvidence,
} from "../../hook/query/useBilling";
import {
  IBillingCustomerInvoice,
  IBillingPayment,
  IBillingRound,
  IPaymentVerificationSummary,
  TPaymentMethod,
} from "../../compiler/billing.interface";
import {
  canOpenCustomerPaymentEvidenceDialog,
  getBillingRoundUuid,
  getScopedBillingCustomerCode,
  getScopedCustomerRoundOptions,
  isBillingCustomerCodeAllowed,
} from "../Billing/billingPaymentSelection";

const formatCurrency = (amount: number) =>
  Number(amount || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = dayjs(value);
  return date.isValid() ? formatThaiCalendarDate(value, "DD/MM/BBBB") : value;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = dayjs(value);
  return date.isValid() ? formatBangkokDateTime(value, "DD/MM/BBBB HH:mm") : value;
};

const toRoundKey = (round: IBillingRound) =>
  [round.start_date, round.end_date, round.round_label, round.customer_code || ""].join("|");

const getInvoicePaymentStatusChip = (status?: string) => {
  if (status === "paid") return <Chip label="ชำระแล้ว" size="small" color="success" />;
  if (status === "partial") return <Chip label="ชำระบางส่วน" size="small" color="warning" />;
  return <Chip label="ค้างชำระ" size="small" color="error" variant="outlined" />;
};

const getPaymentReviewStatusChip = (status?: string) => {
  if (status === "approved" || status === "completed" || status === "confirmed") {
    return <Chip label="อนุมัติแล้ว" size="small" color="success" />;
  }
  if (status === "rejected") return <Chip label="ไม่อนุมัติ" size="small" color="error" />;
  if (status === "cancelled") return <Chip label="ยกเลิก" size="small" color="default" />;
  return <Chip label="รอตรวจสอบ" size="small" color="warning" variant="outlined" />;
};

const getPaymentVerificationStatus = (summary?: IPaymentVerificationSummary) => {
  if (!summary) return { label: "รอตรวจยอด", color: "default" as const };
  if (summary.amount_matches_payment === true) {
    return { label: "ยอดหลักฐานตรง", color: "success" as const };
  }
  if (summary.amount_matches_payment === false) {
    return { label: "ยอดหลักฐานไม่ตรง", color: "warning" as const };
  }
  if (summary.evidence_file_count === 0) return { label: "ไม่มีหลักฐาน", color: "error" as const };
  return { label: "รอ OCR/metadata", color: "info" as const };
};

const CustomerBillingInvoiceListPage: React.FC = () => {
  const location = useLocation();
  const query = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryCustomerCode = query.get("customerNo") || query.get("customer_code") || "";
  const queryCustomerName = query.get("customerName") || "";
  const queryRoundLabel = query.get("round") || "";
  const queryStartDate = query.get("start_date") || "";
  const queryEndDate = query.get("end_date") || "";

  const [selectedCustomerCode, setSelectedCustomerCode] = React.useState(queryCustomerCode);
  const [roundKey, setRoundKey] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = React.useState<
    "all" | "paid" | "partial" | "pending"
  >("all");
  const [selectedInvoiceUuids, setSelectedInvoiceUuids] = React.useState<string[]>([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = React.useState(false);
  const [paymentAmountInput, setPaymentAmountInput] = React.useState("");
  const [paymentMethodInput, setPaymentMethodInput] = React.useState<TPaymentMethod>("transfer");
  const [paymentReferenceInput, setPaymentReferenceInput] = React.useState("");
  const [paymentNotesInput, setPaymentNotesInput] = React.useState("");
  const [evidenceFiles, setEvidenceFiles] = React.useState<File[]>([]);
  const [additionalEvidencePaymentUuid, setAdditionalEvidencePaymentUuid] = React.useState("");
  const [additionalEvidencePaymentLabel, setAdditionalEvidencePaymentLabel] = React.useState("");
  const [additionalEvidenceRejectionReason, setAdditionalEvidenceRejectionReason] =
    React.useState("");
  const [additionalEvidenceRejectedBy, setAdditionalEvidenceRejectedBy] = React.useState("");
  const [additionalEvidenceRejectedAt, setAdditionalEvidenceRejectedAt] = React.useState("");
  const [additionalEvidenceFiles, setAdditionalEvidenceFiles] = React.useState<File[]>([]);

  const { data: customersSummaryResponse, isLoading: isCustomersLoading } =
    useGetCustomersBillingSummary({ enforce_customer_scope: true });
  const customerOptions = customersSummaryResponse?.data?.customers ?? [];
  const hasLoadedCustomerScope = !!customersSummaryResponse && !isCustomersLoading;
  const { data: roundsResponse, isLoading: isRoundsLoading } = useGetBillingRounds({
    limit: 500,
    enforce_customer_scope: true,
  });
  const allRounds = roundsResponse?.data?.rounds ?? [];
  const createPaymentMutation = useCreateBillingPayment();
  const uploadEvidenceMutation = useUploadPaymentEvidence();

  React.useEffect(() => {
    if (!hasLoadedCustomerScope) return;

    const scopedCustomerCode = getScopedBillingCustomerCode(
      selectedCustomerCode || queryCustomerCode,
      customerOptions
    );
    if (selectedCustomerCode === scopedCustomerCode) return;

    setSelectedCustomerCode(scopedCustomerCode);
    setRoundKey("");
    setSelectedInvoiceUuids([]);
  }, [customerOptions, hasLoadedCustomerScope, queryCustomerCode, selectedCustomerCode]);

  const isSelectedCustomerAllowed = React.useMemo(
    () => isBillingCustomerCodeAllowed(selectedCustomerCode, customerOptions),
    [customerOptions, selectedCustomerCode]
  );
  const scopedCustomerCode =
    hasLoadedCustomerScope && isSelectedCustomerAllowed ? selectedCustomerCode : "";

  const activeCustomer = React.useMemo(
    () => customerOptions.find((item) => item.customer_code === scopedCustomerCode),
    [customerOptions, scopedCustomerCode]
  );
  const activeCustomerName =
    activeCustomer?.customer_name ||
    (queryCustomerCode === scopedCustomerCode ? queryCustomerName : "") ||
    "";

  const roundOptions = React.useMemo(() => {
    if (!scopedCustomerCode) return [];
    return getScopedCustomerRoundOptions(allRounds, scopedCustomerCode).sort((a, b) =>
      b.start_date.localeCompare(a.start_date)
    );
  }, [allRounds, scopedCustomerCode]);

  React.useEffect(() => {
    setSelectedInvoiceUuids([]);
    if (roundOptions.length === 0) {
      setRoundKey("");
      return;
    }

    const queryMatchedRound =
      roundOptions.find(
        (round) =>
          round.start_date === queryStartDate &&
          round.end_date === queryEndDate &&
          (!queryRoundLabel || round.round_label === queryRoundLabel)
      ) || roundOptions.find((round) => round.round_label === queryRoundLabel);

    setRoundKey((prev) => {
      if (roundOptions.some((round) => toRoundKey(round) === prev)) return prev;
      return toRoundKey(queryMatchedRound || roundOptions[0]);
    });
  }, [queryEndDate, queryRoundLabel, queryStartDate, roundOptions]);

  const selectedRound = React.useMemo(
    () => roundOptions.find((round) => toRoundKey(round) === roundKey) || roundOptions[0],
    [roundKey, roundOptions]
  );
  const activeStartDate = selectedRound?.start_date || "";
  const activeEndDate = selectedRound?.end_date || "";
  const activeRoundLabel = selectedRound?.round_label || "";
  const selectedBillingRoundUuid = getBillingRoundUuid(selectedRound);
  const paymentsRoundKey =
    activeStartDate && activeEndDate ? `${activeStartDate} - ${activeEndDate}` : undefined;

  const { data: invoicesResponse, isLoading: isInvoicesLoading } = useGetBillingCustomerInvoices({
    customer_code: scopedCustomerCode,
    start_date: activeStartDate,
    end_date: activeEndDate,
    search: search.trim() || undefined,
    payment_status: paymentStatusFilter === "all" ? undefined : paymentStatusFilter,
    limit: 500,
    enforce_customer_scope: true,
  });
  const invoices = invoicesResponse?.data?.invoices ?? [];
  const invoiceSummary = invoicesResponse?.data?.summary;

  const { data: paymentsResponse, isLoading: isPaymentsLoading } = useGetBillingPayments(
    {
      billing_round_uuid: selectedBillingRoundUuid,
      customer_code: scopedCustomerCode,
      billing_round: paymentsRoundKey,
      limit: 50,
      enforce_customer_scope: true,
    },
    !!scopedCustomerCode && !!paymentsRoundKey && !!selectedBillingRoundUuid
  );
  const payments = paymentsResponse?.data?.payments ?? [];

  const selectedRows = React.useMemo(
    () => invoices.filter((row) => selectedInvoiceUuids.includes(row.uuid)),
    [invoices, selectedInvoiceUuids]
  );
  const selectableInvoices = React.useMemo(
    () => invoices.filter((row) => Number(row.outstanding_amount || 0) > 0),
    [invoices]
  );
  const selectedOutstandingTotal = selectedRows.reduce(
    (sum, row) => sum + Number(row.outstanding_amount || 0),
    0
  );
  const isAllVisibleSelected =
    selectableInvoices.length > 0 &&
    selectableInvoices.every((row) => selectedInvoiceUuids.includes(row.uuid));
  const isSomeVisibleSelected =
    selectableInvoices.some((row) => selectedInvoiceUuids.includes(row.uuid)) &&
    !isAllVisibleSelected;
  const canOpenPaymentDialog = canOpenCustomerPaymentEvidenceDialog({
    scopedCustomerCode,
    billingRoundUuid: selectedBillingRoundUuid,
    paymentsRoundKey,
    selectedInvoiceUuids,
    selectedOutstandingTotal,
    isLoading: createPaymentMutation.isLoading,
  });

  const toggleSelectAllVisible = () => {
    if (isAllVisibleSelected) {
      setSelectedInvoiceUuids((prev) =>
        prev.filter((uuid) => !selectableInvoices.some((row) => row.uuid === uuid))
      );
      return;
    }

    setSelectedInvoiceUuids((prev) =>
      Array.from(new Set([...prev, ...selectableInvoices.map((row) => row.uuid)]))
    );
  };

  const toggleSelectOne = (invoice: IBillingCustomerInvoice) => {
    if (Number(invoice.outstanding_amount || 0) <= 0) return;
    setSelectedInvoiceUuids((prev) =>
      prev.includes(invoice.uuid)
        ? prev.filter((uuid) => uuid !== invoice.uuid)
        : [...prev, invoice.uuid]
    );
  };

  const openPaymentDialog = () => {
    if (!canOpenPaymentDialog) return;

    setPaymentAmountInput(String(Math.round(selectedOutstandingTotal * 100) / 100));
    setPaymentMethodInput("transfer");
    setPaymentReferenceInput("");
    setPaymentNotesInput("");
    setEvidenceFiles([]);
    setPaymentDialogOpen(true);
  };

  const closePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setEvidenceFiles([]);
  };

  const handleFileChange = (files: FileList | null) => {
    if (!files?.length) return;
    setEvidenceFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const removeEvidenceFile = (index: number) => {
    setEvidenceFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const openAdditionalEvidenceDialog = (payment: IBillingPayment) => {
    const paymentUuid = payment.uuid || payment.payment_uuid || "";
    if (!paymentUuid) return;

    setAdditionalEvidencePaymentUuid(paymentUuid);
    setAdditionalEvidencePaymentLabel(payment.payment_id || paymentUuid);
    setAdditionalEvidenceRejectionReason(
      payment.status === "rejected" ? payment.rejection_reason || "" : ""
    );
    setAdditionalEvidenceRejectedBy(payment.status === "rejected" ? payment.rejected_by || "" : "");
    setAdditionalEvidenceRejectedAt(payment.status === "rejected" ? payment.rejected_at || "" : "");
    setAdditionalEvidenceFiles([]);
  };

  const closeAdditionalEvidenceDialog = () => {
    if (uploadEvidenceMutation.isLoading) return;
    setAdditionalEvidencePaymentUuid("");
    setAdditionalEvidencePaymentLabel("");
    setAdditionalEvidenceRejectionReason("");
    setAdditionalEvidenceRejectedBy("");
    setAdditionalEvidenceRejectedAt("");
    setAdditionalEvidenceFiles([]);
  };

  const handleAdditionalEvidenceFileChange = (files: FileList | null) => {
    if (!files?.length) return;
    setAdditionalEvidenceFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const removeAdditionalEvidenceFile = (index: number) => {
    setAdditionalEvidenceFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const saveAdditionalEvidence = async () => {
    if (!additionalEvidencePaymentUuid || additionalEvidenceFiles.length === 0) return;

    await uploadEvidenceMutation.mutateAsync({
      paymentUuid: additionalEvidencePaymentUuid,
      files: additionalEvidenceFiles,
      evidenceSource: "customer",
      enforceCustomerScope: true,
    });

    closeAdditionalEvidenceDialog();
  };

  const savePaymentEvidence = async () => {
    const totalPaidAmount = Number(paymentAmountInput || 0);
    if (
      !scopedCustomerCode ||
      !paymentsRoundKey ||
      !selectedBillingRoundUuid ||
      selectedInvoiceUuids.length === 0 ||
      !Number.isFinite(totalPaidAmount) ||
      totalPaidAmount <= 0 ||
      totalPaidAmount > selectedOutstandingTotal ||
      evidenceFiles.length === 0
    ) {
      return;
    }

    await createPaymentMutation.mutateAsync({
      invoice_uuids: selectedInvoiceUuids,
      customer_code: scopedCustomerCode,
      total_paid_amount: totalPaidAmount,
      payment_method: paymentMethodInput,
      payment_date: dayjs().format("YYYY-MM-DD"),
      payment_reference: paymentReferenceInput.trim() || undefined,
      notes: paymentNotesInput.trim() || undefined,
      billing_round: paymentsRoundKey,
      billing_round_uuid: selectedBillingRoundUuid,
      evidence_metadata: evidenceFiles.map((file) => ({
        file_name: file.name,
        transfer_amount: evidenceFiles.length === 1 ? totalPaidAmount : undefined,
        transfer_date: dayjs().format("YYYY-MM-DD"),
        transfer_reference: paymentReferenceInput.trim() || undefined,
        notes: paymentNotesInput.trim() || undefined,
      })),
      files: evidenceFiles,
      evidence_source: "customer",
      enforce_customer_scope: true,
    });

    closePaymentDialog();
    setSelectedInvoiceUuids([]);
  };

  const invoiceHeaders = [
    {
      name: (
        <Checkbox
          checked={isAllVisibleSelected}
          indeterminate={isSomeVisibleSelected}
          onChange={toggleSelectAllVisible}
          size="small"
        />
      ),
      props: { align: "center" as const, width: "4%" },
    },
    { name: "InvoiceNo", props: { align: "left" as const, width: "14%" } },
    { name: "วันที่", props: { align: "center" as const, width: "10%" } },
    { name: "สาขา", props: { align: "left" as const, width: "22%" } },
    { name: "จำนวนเงิน", props: { align: "right" as const, width: "12%" } },
    { name: "ชำระแล้ว", props: { align: "right" as const, width: "12%" } },
    { name: "ค้างชำระ", props: { align: "right" as const, width: "12%" } },
    { name: "สถานะ", props: { align: "center" as const, width: "10%" } },
  ];

  const paymentHeaders = [
    { name: "เลขที่รับชำระ", props: { align: "left" as const, width: "20%" } },
    { name: "วันที่บันทึก", props: { align: "center" as const, width: "14%" } },
    { name: "ยอดโอน", props: { align: "right" as const, width: "14%" } },
    { name: "เลขอ้างอิง", props: { align: "left" as const, width: "18%" } },
    { name: "หลักฐาน", props: { align: "center" as const, width: "10%" } },
    { name: "สถานะ", props: { align: "center" as const, width: "12%" } },
    { name: "เครื่องมือ", props: { align: "center" as const, width: "12%" } },
  ];

  const paymentAmount = Number(paymentAmountInput || 0);
  const canSubmitPayment =
    selectedInvoiceUuids.length > 0 &&
    !!scopedCustomerCode &&
    evidenceFiles.length > 0 &&
    Number.isFinite(paymentAmount) &&
    paymentAmount > 0 &&
    paymentAmount <= selectedOutstandingTotal &&
    !createPaymentMutation.isLoading;
  const canSubmitAdditionalEvidence =
    !!additionalEvidencePaymentUuid &&
    additionalEvidenceFiles.length > 0 &&
    !uploadEvidenceMutation.isLoading;

  return (
    <>
      <NavigationBar
        title="รายการบิลลูกค้า"
        tools={
          <Stack direction="row" spacing={1} alignItems="center">
            {activeRoundLabel && (
              <Chip label={`รอบบิล: ${activeRoundLabel}`} color="info" variant="outlined" />
            )}
            {scopedCustomerCode && (
              <Chip label={`ลูกค้า: ${scopedCustomerCode}`} color="success" variant="outlined" />
            )}
          </Stack>
        }
      />

      <Stack spacing={2} sx={{ p: 2 }}>
        <ActionPaper>
          <Stack spacing={2} sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ md: "center" }}
            >
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>บริษัทลูกค้า</InputLabel>
                <Select
                  value={isSelectedCustomerAllowed ? selectedCustomerCode : ""}
                  label="บริษัทลูกค้า"
                  disabled={isCustomersLoading || customerOptions.length <= 1}
                  onChange={(event) => {
                    setSelectedCustomerCode(event.target.value);
                    setRoundKey("");
                    setSelectedInvoiceUuids([]);
                  }}
                >
                  <MenuItem value="" disabled>
                    เลือกบริษัทลูกค้า
                  </MenuItem>
                  {customerOptions.map((customer) => (
                    <MenuItem key={customer.customer_code} value={customer.customer_code}>
                      {customer.customer_code} - {customer.customer_name || "-"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>รอบบิล</InputLabel>
                <Select
                  value={roundKey}
                  label="รอบบิล"
                  disabled={isRoundsLoading || roundOptions.length === 0}
                  onChange={(event) => {
                    setRoundKey(event.target.value);
                    setSelectedInvoiceUuids([]);
                  }}
                >
                  {roundOptions.map((round) => (
                    <MenuItem key={toRoundKey(round)} value={toRoundKey(round)}>
                      {round.round_label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                size="small"
                label="ค้นหา Invoice / สาขา"
                sx={{ minWidth: 240 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>สถานะ</InputLabel>
                <Select
                  value={paymentStatusFilter}
                  label="สถานะ"
                  onChange={(event) =>
                    setPaymentStatusFilter(
                      event.target.value as "all" | "paid" | "partial" | "pending"
                    )
                  }
                >
                  <MenuItem value="all">ทั้งหมด</MenuItem>
                  <MenuItem value="pending">ค้างชำระ</MenuItem>
                  <MenuItem value="partial">ชำระบางส่วน</MenuItem>
                  <MenuItem value="paid">ชำระแล้ว</MenuItem>
                </Select>
              </FormControl>
              <Box flex={1} />
              <Button
                variant="contained"
                startIcon={<CloudUploadOutlinedIcon />}
                disabled={!canOpenPaymentDialog}
                onClick={openPaymentDialog}
              >
                แนบหลักฐาน
              </Button>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ReceiptLongIcon color="primary" />
                <Typography>
                  Invoice <b>{Number(invoiceSummary?.total_invoices || 0).toLocaleString()}</b> ใบ
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PaidIcon color="success" />
                <Typography>
                  ยอดรวม <b>{formatCurrency(invoiceSummary?.total_amount || 0)}</b> บาท
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PaidIcon color="error" />
                <Typography color="error.main">
                  ค้างชำระ <b>{formatCurrency(invoiceSummary?.outstanding_amount || 0)}</b> บาท
                </Typography>
              </Stack>
              {activeCustomerName && (
                <Chip label={activeCustomerName} size="small" color="default" variant="outlined" />
              )}
            </Stack>

            {!scopedCustomerCode && !isCustomersLoading && (
              <Alert severity="info">
                ไม่พบบริษัทลูกค้าที่สามารถดูรายการวางบิลได้
                กรุณาติดต่อผู้ดูแลระบบเพื่อตรวจสอบการผูกบริษัท
              </Alert>
            )}
            {scopedCustomerCode && roundOptions.length === 0 && !isRoundsLoading && (
              <Alert severity="info">ไม่พบรอบบิลของบริษัทนี้</Alert>
            )}
          </Stack>

          <Box id="customer-billing-invoice-table">
            <BasicTable
              headers={invoiceHeaders}
              isLoading={isInvoicesLoading}
              limit={invoices.length || 10}
              rows={invoices.map((invoice) => {
                const outstandingAmount = Number(invoice.outstanding_amount || 0);
                return [
                  {
                    ele: (
                      <Checkbox
                        checked={selectedInvoiceUuids.includes(invoice.uuid)}
                        disabled={outstandingAmount <= 0}
                        onChange={() => toggleSelectOne(invoice)}
                        size="small"
                      />
                    ),
                    props: { align: "center" as const },
                  },
                  { ele: invoice.invoice_number, props: { align: "left" as const } },
                  { ele: formatDate(invoice.invoice_date), props: { align: "center" as const } },
                  {
                    ele: `${invoice.branch_code || "-"} ${invoice.branch_name || ""}`.trim(),
                    props: { align: "left" as const },
                  },
                  { ele: formatCurrency(invoice.total_amount), props: { align: "right" as const } },
                  {
                    ele: formatCurrency(invoice.paid_amount),
                    props: {
                      align: "right" as const,
                      sx: { color: "success.main", fontWeight: 600 },
                    },
                  },
                  {
                    ele: formatCurrency(invoice.outstanding_amount),
                    props: {
                      align: "right" as const,
                      sx: {
                        color: outstandingAmount > 0 ? "error.main" : "text.primary",
                        fontWeight: 700,
                      },
                    },
                  },
                  {
                    ele: getInvoicePaymentStatusChip(invoice.payment_status),
                    props: { align: "center" as const },
                  },
                ];
              })}
            />
          </Box>
        </ActionPaper>

        <ActionPaper>
          <Stack spacing={1.5} sx={{ p: 2 }}>
            <Typography fontWeight={700}>ประวัติการแนบหลักฐาน</Typography>
            {payments.length === 0 && !isPaymentsLoading && (
              <Alert severity="info">ยังไม่มีรายการชำระเงินในรอบนี้</Alert>
            )}
          </Stack>
          <BasicTable
            headers={paymentHeaders}
            isLoading={isPaymentsLoading}
            limit={payments.length || 5}
            rows={payments.map((payment) => {
              const paymentUuid = payment.uuid || payment.payment_uuid || "";
              const canUploadMoreEvidence =
                !!paymentUuid && ["submitted", "rejected"].includes(payment.status);
              const verificationStatus = getPaymentVerificationStatus(payment.verification_summary);
              const rejectionReason =
                payment.status === "rejected" ? payment.rejection_reason || "" : "";

              return [
                { ele: payment.payment_id, props: { align: "left" as const } },
                { ele: formatDate(payment.payment_date), props: { align: "center" as const } },
                {
                  ele: formatCurrency(payment.total_paid_amount),
                  props: { align: "right" as const },
                },
                { ele: payment.payment_reference || "-", props: { align: "left" as const } },
                {
                  ele: (
                    <Stack spacing={0.5} justifyContent="center" alignItems="center">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AttachFileIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {(payment.evidenceFiles || payment.evidence_files || []).length}
                        </Typography>
                      </Stack>
                      <Chip
                        label={verificationStatus.label}
                        color={verificationStatus.color}
                        size="small"
                        variant={verificationStatus.color === "default" ? "outlined" : "filled"}
                      />
                    </Stack>
                  ),
                  props: { align: "center" as const },
                },
                {
                  ele: (
                    <Stack spacing={0.5} alignItems="center">
                      {getPaymentReviewStatusChip(payment.status)}
                      {rejectionReason && (
                        <Typography
                          variant="caption"
                          color="error.main"
                          sx={{ maxWidth: 180, wordBreak: "break-word", lineHeight: 1.35 }}
                        >
                          เหตุผล: {rejectionReason}
                        </Typography>
                      )}
                    </Stack>
                  ),
                  props: { align: "center" as const },
                },
                {
                  ele: canUploadMoreEvidence ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUploadOutlinedIcon />}
                      onClick={() => openAdditionalEvidenceDialog(payment)}
                    >
                      แนบเพิ่ม
                    </Button>
                  ) : (
                    "-"
                  ),
                  props: { align: "center" as const },
                },
              ];
            })}
          />
        </ActionPaper>
      </Stack>

      <Dialog open={paymentDialogOpen} onClose={closePaymentDialog} maxWidth="md" fullWidth>
        <DialogTitle>แนบหลักฐานการโอนเงิน</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              เลือกไว้ {selectedInvoiceUuids.length.toLocaleString()} ใบ ยอดค้างชำระรวม{" "}
              {formatCurrency(selectedOutstandingTotal)} บาท
            </Alert>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <TextField
                value={paymentAmountInput}
                onChange={(event) => setPaymentAmountInput(event.target.value)}
                size="small"
                label="ยอดโอน"
                type="number"
                fullWidth
              />
              <FormControl size="small" fullWidth>
                <InputLabel>วิธีชำระ</InputLabel>
                <Select
                  value={paymentMethodInput}
                  label="วิธีชำระ"
                  onChange={(event) => setPaymentMethodInput(event.target.value as TPaymentMethod)}
                >
                  <MenuItem value="transfer">โอนเงิน</MenuItem>
                  <MenuItem value="cheque">เช็ค</MenuItem>
                  <MenuItem value="cash">เงินสด</MenuItem>
                  <MenuItem value="other">อื่น ๆ</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <TextField
              value={paymentReferenceInput}
              onChange={(event) => setPaymentReferenceInput(event.target.value)}
              size="small"
              label="เลขอ้างอิงการชำระ"
              fullWidth
            />
            <TextField
              value={paymentNotesInput}
              onChange={(event) => setPaymentNotesInput(event.target.value)}
              size="small"
              label="หมายเหตุ"
              minRows={2}
              multiline
              fullWidth
            />
            <Stack spacing={1}>
              <Button variant="outlined" component="label" startIcon={<CloudUploadOutlinedIcon />}>
                เลือกไฟล์หลักฐาน
                <input
                  hidden
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(event) => {
                    handleFileChange(event.target.files);
                    event.target.value = "";
                  }}
                />
              </Button>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {evidenceFiles.map((file, index) => (
                  <Chip
                    key={`${file.name}-${index}`}
                    label={file.name}
                    variant="outlined"
                    onDelete={() => removeEvidenceFile(index)}
                  />
                ))}
              </Stack>
              {evidenceFiles.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  กรุณาแนบไฟล์รูปภาพหรือ PDF อย่างน้อย 1 ไฟล์
                </Typography>
              )}
            </Stack>
            {paymentAmount > selectedOutstandingTotal && (
              <Alert severity="warning">ยอดโอนต้องไม่เกินยอดค้างชำระของรายการที่เลือก</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentDialog} disabled={createPaymentMutation.isLoading}>
            ยกเลิก
          </Button>
          <Button variant="contained" onClick={savePaymentEvidence} disabled={!canSubmitPayment}>
            {createPaymentMutation.isLoading ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!additionalEvidencePaymentUuid}
        onClose={closeAdditionalEvidenceDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>แนบหลักฐานเพิ่มเติม</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              รายการรับชำระ {additionalEvidencePaymentLabel || "-"}
              หากรายการเดิมถูกไม่อนุมัติ ระบบจะส่งกลับไปรอตรวจสอบอีกครั้ง
            </Alert>
            {additionalEvidenceRejectionReason && (
              <Alert severity="warning">
                <Stack spacing={0.5}>
                  <Typography fontWeight={700}>เหตุผลที่ไม่อนุมัติ</Typography>
                  <Typography>{additionalEvidenceRejectionReason}</Typography>
                  {(additionalEvidenceRejectedBy || additionalEvidenceRejectedAt) && (
                    <Typography variant="body2" color="text.secondary">
                      ผู้ตรวจ: {additionalEvidenceRejectedBy || "-"} | เวลา:{" "}
                      {formatDateTime(additionalEvidenceRejectedAt)}
                    </Typography>
                  )}
                </Stack>
              </Alert>
            )}
            <Button variant="outlined" component="label" startIcon={<CloudUploadOutlinedIcon />}>
              เลือกไฟล์หลักฐาน
              <input
                hidden
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={(event) => {
                  handleAdditionalEvidenceFileChange(event.target.files);
                  event.target.value = "";
                }}
              />
            </Button>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {additionalEvidenceFiles.map((file, index) => (
                <Chip
                  key={`${file.name}-${index}`}
                  label={file.name}
                  variant="outlined"
                  onDelete={() => removeAdditionalEvidenceFile(index)}
                />
              ))}
            </Stack>
            {additionalEvidenceFiles.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                กรุณาแนบไฟล์รูปภาพหรือ PDF อย่างน้อย 1 ไฟล์
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeAdditionalEvidenceDialog}
            disabled={uploadEvidenceMutation.isLoading}
          >
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            onClick={saveAdditionalEvidence}
            disabled={!canSubmitAdditionalEvidence}
          >
            {uploadEvidenceMutation.isLoading ? "กำลังอัปโหลด..." : "บันทึก"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerBillingInvoiceListPage;
